import React, { useState, useMemo, useCallback } from 'react';
import { FaPlus, FaMinus, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const ImageViewer = ({ modelvalues }) => {
  // Ensure images is defined and an array
  const images = useMemo(
    () => modelvalues?.bill?.uploadedBills?.map((bill) => bill.bill_url) || [],
    [modelvalues]
  );

  // Set initial states
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [lastMouseX, setLastMouseX] = useState(0);
  const [lastMouseY, setLastMouseY] = useState(0);

  // Reset zoom and position
  const resetZoomAndPosition = useCallback(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  }, []);

  // Handle next image
  const handleNext = useCallback(() => {
    if (images.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      resetZoomAndPosition();
    }
  }, [images, resetZoomAndPosition]);

  // Handle previous image
  const handlePrevious = useCallback(() => {
    if (images.length > 0) {
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + images.length) % images.length
      );
      resetZoomAndPosition();
    }
  }, [images, resetZoomAndPosition]);

  // Handle zoom in
  const handleZoomIn = useCallback(() => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 3));
  }, []);

  // Handle zoom out
  const handleZoomOut = useCallback(() => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 1));
  }, []);

  // Handle mouse down
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setLastMouseX(e.clientX);
    setLastMouseY(e.clientY);
  }, []);

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        const dx = e.clientX - lastMouseX;
        const dy = e.clientY - lastMouseY;
        setTranslateX((prevX) => prevX + dx);
        setTranslateY((prevY) => prevY + dy);
        setLastMouseX(e.clientX);
        setLastMouseY(e.clientY);
      }
    },
    [isDragging, lastMouseX, lastMouseY]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Return early if no images are available
  if (images.length === 0) {
    return <div>No images available</div>;
  }

  return (
    <div className='image-viewer'>
      <div className='zoom-controls'>
        <button onClick={handleZoomIn}>
          <FaPlus />
        </button>
        <button onClick={handleZoomOut}>
          <FaMinus />
        </button>
      </div>
      <div
        className='image-container'
        style={{
          transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <img src={images[currentIndex]} alt='invoice' />
      </div>
      {images.length > 1 && (
        <div className='navigation-buttons'>
          <button className='nav-button' onClick={handlePrevious}>
            <FaArrowLeft />
          </button>
          <button className='nav-button' onClick={handleNext}>
            <FaArrowRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
